import dayjs from "dayjs";
import React from "react";

export interface Report {
  id: string;
  name: string;
  description: string;
  category: string;
  createdBy: string;
  lastUpdated: string;
}

export interface ReportColumn {
  title: string;
  dataIndex: string;
  key: string;
  width?: number;
  render?: (text: any, record: any, index?: number) => React.ReactNode;
  fixed?: "left" | "right";
}

export interface ReportFilter {
  type: "date" | "select" | "multiSelect" | "range" | "text";
  label: string;
  key: string;
  options?: { label: string; value: any }[];
  placeholder?: string;
}

export interface ReportTab {
  key: string;
  label: string;
  columns: ReportColumn[];
  data?: any[];
}

export interface ReportConfig {
  id: string;
  title: string;
  columns?: ReportColumn[];
  data?: any[];
  tabs?: ReportTab[];
  filters?: ReportFilter[];
  exportable?: boolean;
  drillDowns?: string[];
}

// Work Order Reports Data
export const workOrderReports: Report[] = [
  {
    id: "wo-portfolio-summary",
    name: "Work Order Portfolio Summary",
    description:
      "Leadership-level overview of all WOs at portfolio level with KPIs and budget variance",
    category: "Master Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "wo-status-distribution",
    name: "Work Order Status Distribution",
    description:
      "Distribution of work orders by status across projects and contractors",
    category: "Status Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "wo-executed-pending-approved",
    name: "Executed WOs - Pending vs Approved",
    description:
      "Detailed view of executed work orders showing pending and approved items",
    category: "Execution Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "payments-released-summary",
    name: "Payments Released Summary",
    description: "Portfolio to voucher level payment tracking and summary",
    category: "Payment Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "budget-variance-report",
    name: "Budget Variance - Executed & In-Progress",
    description:
      "Variance analysis between budget allocated and actual/forecast costs",
    category: "Budget Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "master-status-counts",
    name: "Master Status Counts",
    description:
      "Overview of work order counts by status with contractor breakdown",
    category: "Summary Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "internal-approval-queue",
    name: "Internal Approval Queue",
    description: "List of WOs awaiting internal approvals with SLA tracking",
    category: "Approval Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "contractor-acceptance-tracker",
    name: "Contractor Acceptance Tracker",
    description: "Monitor contractor acceptance status of issued work orders",
    category: "Contractor Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "work-execution-pending-updates",
    name: "Work Execution - Pending Updates",
    description: "Identify tasks where contractors have not updated progress",
    category: "Execution Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "work-execution-approved-remaining",
    name: "Work Execution - Approved vs Remaining",
    description: "Execution vs approvals pending with balance to execute",
    category: "Execution Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "version-control-amendments",
    name: "Version Control & Amendments Register",
    description: "Track scope, cost, and time changes to work orders",
    category: "Change Management",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "ra-bills-status-register",
    name: "RA Bills Status Register",
    description:
      "Consolidated register of all Running Account bills raised under WOs",
    category: "Billing Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "ra-bill-aging",
    name: "RA Bill Aging",
    description:
      "Track age of RA Bills pending approval/payment with aging buckets",
    category: "Aging Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "deductions-ledger",
    name: "Deductions Ledger",
    description:
      "Track deductions against each WO and RA Bill including retention",
    category: "Financial Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "payments-register",
    name: "Payments Register",
    description: "Ledger of all payments released under work orders",
    category: "Payment Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "outstanding-aging",
    name: "Outstanding & Aging",
    description: "Highlight pending contractor dues with aging analysis",
    category: "Aging Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "wo-vs-budget",
    name: "WO vs Budget",
    description:
      "Track allocated budget vs actual WO commitments with variance analysis",
    category: "Budget Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "tax-summary",
    name: "Tax Summary",
    description:
      "Consolidated view of all taxes deducted & paid across work orders",
    category: "Tax Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "audit-trail",
    name: "Audit Trail",
    description:
      "Maintain log of all actions on WOs with user and timestamp tracking",
    category: "Audit Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
  {
    id: "contractor-ledger",
    name: "Contractor Ledger",
    description:
      "Track all financial transactions for contractors including RA Bills, payments, debits and credits",
    category: "Financial Reports",
    createdBy: "System Generated",
    lastUpdated: "2024-08-20",
  },
];

// Report configurations
export const reportConfigs: { [key: string]: ReportConfig } = {
  "wo-portfolio-summary": {
    id: "wo-portfolio-summary",
    title: "Work Order Portfolio Summary (Master Report)",
    columns: [
      {
        title: "Project",
        dataIndex: "project",
        key: "project",
        fixed: "left",
        width: 280,
      },
      {
        title: "Budget (₹)",
        dataIndex: "budgetAllocatedValue",
        key: "budgetAllocatedValue",
        width: 150,
      },
      {
        title: "Total Work Orders (₹)",
        dataIndex: "totalWorkOrdersValue",
        key: "totalWorkOrdersValue",
        width: 180,
      },
      {
        title: "Works Executed (₹)",
        dataIndex: "worksExecutedValue",
        key: "worksExecutedValue",
        width: 140,
      },
      {
        title: "Works Under Approval (₹)",
        dataIndex: "worksUnderApprovalValue",
        key: "worksUnderApprovalValue",
        width: 180,
      },
      {
        title: "Approved RA Bills (₹)",
        dataIndex: "approvedRABillsValue",
        key: "approvedRABillsValue",
        width: 180,
      },
      {
        title: "Payments Release (₹)",
        dataIndex: "paymentsReleased",
        key: "paymentsReleased",
        width: 180,
      },
      {
        title: "Outstanding Value (₹)",
        dataIndex: "outstandingValue",
        key: "outstandingValue",
        width: 180,
      },
      {
        title: "Over Due Value (₹)",
        dataIndex: "overDueValue",
        key: "overDueValue",
        width: 180,
      },
      {
        title: "Budget Variance",
        dataIndex: "budgetVarInProgress",
        key: "budgetVarInProgress",
        width: 220,
        render: (_: any, record: any) => {
          const value = record.budgetVarInProgress;
          const pct = record.budgetUtilizationPct;
          const text = pct || "";
          return (
            <span style={{ color: text.startsWith("+") ? "green" : "red" }}>
              {value} {pct && `(${pct})`}
            </span>
          );
        },
      },
      {
        title: "Last Updated",
        dataIndex: "lastUpdate",
        key: "lastUpdate",
        width: 120,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
    ],
    filters: [
      {
        type: "date",
        label: "Date Range",
        key: "dateRange",
        placeholder: "Select date range",
      },
      {
        type: "multiSelect",
        label: "Project/Site/Phase",
        key: "projects",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
          { label: "Campus C", value: "campus-c" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
          { label: "PQR Plumbing", value: "pqr-plumbing" },
        ],
      },
      {
        type: "select",
        label: "Contract Type",
        key: "contractType",
        options: [
          { label: "Fixed Price", value: "fixed-price" },
          { label: "Time & Material", value: "time-material" },
          { label: "Cost Plus", value: "cost-plus" },
        ],
      },
      {
        type: "range",
        label: "Budget/Value Bands",
        key: "budgetRange",
        placeholder: "Enter budget range",
      },
    ],
    exportable: true,
    drillDowns: [
      "wo-status-distribution",
      "budget-variance-report",
      "payments-released-summary",
    ],
  },
  "wo-status-distribution": {
    id: "wo-status-distribution",
    title: "Work Order Status Distribution (Project/Contractor)",
    columns: [
      {
        title: "Project",
        dataIndex: "project",
        key: "project",
        fixed: "left",
        width: 250,
      },
      {
        title: "Budget Allocated (₹)",
        dataIndex: "budgetAllocated",
        key: "budgetAllocated",
        width: 150,
      },
      {
        title: "WO Value (₹)",
        dataIndex: "woValue",
        key: "woValue",
        width: 140,
      },
      { title: "Draft", dataIndex: "draft", key: "draft", width: 80 },
      { title: "Approval", dataIndex: "approval", key: "approval", width: 90 },
      { title: "Released", dataIndex: "released", key: "released", width: 90 },
      { title: "Accepted", dataIndex: "accepted", key: "accepted", width: 90 },
      { title: "Active", dataIndex: "active", key: "active", width: 80 },
      { title: "Amended", dataIndex: "amended", key: "amended", width: 90 },
      { title: "Closed", dataIndex: "closed", key: "closed", width: 80 },
      {
        title: "Cancelled",
        dataIndex: "cancelled",
        key: "cancelled",
        width: 90,
      },
      { title: "Total", dataIndex: "total", key: "total", width: 80 },
    ],
    filters: [
      {
        type: "multiSelect",
        label: "Project/Site",
        key: "projects",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
          { label: "Campus C", value: "campus-c" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "multiSelect",
        label: "Status",
        key: "status",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Approval", value: "approval" },
          { label: "Released", value: "released" },
          { label: "Accepted", value: "accepted" },
          { label: "Active", value: "active" },
          { label: "Amended", value: "amended" },
          { label: "Closed", value: "closed" },
          { label: "Cancelled", value: "cancelled" },
        ],
      },
    ],
    exportable: true,
    drillDowns: ["wo-executed-pending-approved"],
  },
  "wo-executed-pending-approved": {
    id: "wo-executed-pending-approved",
    title: "Executed WOs - Pending vs Approved (Detail)",
    tabs: [
      {
        key: "pending",
        label: "Pending Tab (Executed—Awaiting Approval)",
        columns: [
          {
            title: "WO #",
            dataIndex: "woNumber",
            key: "woNumber",
            fixed: "left",
            width: 250,
          },
          {
            title: "Project",
            dataIndex: "project",
            key: "project",
            width: 250,
          },
          {
            title: "Contractor",
            dataIndex: "contractor",
            key: "contractor",
            width: 220,
          },
          {
            title: "WO Value (₹)",
            dataIndex: "woValue",
            key: "woValue",
            width: 130,
          },
          {
            title: "Budget Allocated (₹)",
            dataIndex: "budgetAllocated",
            key: "budgetAllocated",
            width: 160,
          },
          {
            title: "Last Exec Update",
            dataIndex: "lastExecUpdate",
            key: "lastExecUpdate",
            width: 140,
          },
          {
            title: "Cum Exec Qty",
            dataIndex: "cumExecQty",
            key: "cumExecQty",
            width: 120,
          },
          {
            title: "Est. RA Value (₹)",
            dataIndex: "estRAValue",
            key: "estRAValue",
            width: 140,
          },
          {
            title: "Pending With",
            dataIndex: "pendingWith",
            key: "pendingWith",
            width: 120,
          },
          {
            title: "Aging (d)",
            dataIndex: "aging",
            key: "aging",
            width: 100,
            render: (days: number) => (
              <span
                style={{
                  color: days > 5 ? "red" : days > 3 ? "orange" : "green",
                }}
              >
                {days}
              </span>
            ),
          },
        ],
      },
      {
        key: "approved",
        label: "Approved Tab (Executed—Approved)",
        columns: [
          {
            title: "WO #",
            dataIndex: "woNumber",
            key: "woNumber",
            fixed: "left",
            width: 220,
          },
          {
            title: "Project",
            dataIndex: "project",
            key: "project",
            width: 250,
          },
          {
            title: "Contractor",
            dataIndex: "contractor",
            key: "contractor",
            width: 250,
          },
          {
            title: "WO Value (₹)",
            dataIndex: "woValue",
            key: "woValue",
            width: 130,
          },
          {
            title: "Budget Allocated (₹)",
            dataIndex: "budgetAllocated",
            key: "budgetAllocated",
            width: 160,
          },
          {
            title: "Approved Qty",
            dataIndex: "approvedQty",
            key: "approvedQty",
            width: 120,
          },
          {
            title: "Approved Value (₹)",
            dataIndex: "approvedValue",
            key: "approvedValue",
            width: 140,
          },
          {
            title: "Last Approved On",
            dataIndex: "lastApprovedOn",
            key: "lastApprovedOn",
            width: 140,
          },
        ],
      },
    ],
    filters: [
      {
        type: "multiSelect",
        label: "Project/Site",
        key: "projects",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
          { label: "Campus C", value: "campus-c" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "PQR Plumbing", value: "pqr-plumbing" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "range",
        label: "WO Value/Budget Bands",
        key: "valueRange",
        placeholder: "Enter value range",
      },
      {
        type: "date",
        label: "Date Range (Exec update / Approval)",
        key: "dateRange",
      },
      {
        type: "select",
        label: "Aging Buckets",
        key: "aging",
        options: [
          { label: "0-3 days", value: "0-3" },
          { label: "4-7 days", value: "4-7" },
          { label: "8+ days", value: "8+" },
        ],
      },
    ],
    exportable: true,
  },
  "payments-released-summary": {
    id: "payments-released-summary",
    title: "Payments Released Summary (Portfolio → Voucher Level)",
    columns: [
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        fixed: "left",
        width: 150,
      },
      {
        title: "Project",
        dataIndex: "project",
        key: "project",
        width: 250,
      },
      {
        title: 'Approved RA Bills (₹)',
        dataIndex: 'approvedRABillsValue',
        key: 'approvedRABillsValue',
        width: 180,
      },
      {
        title: 'Payments Release (₹)',
        dataIndex: 'paymentsReleaseValue',
        key: 'paymentsReleaseValue',
        width: 150,
      },
      {
        title: "Outstanding Value (₹)",
        dataIndex: "outstanding",
        key: "outstanding",
        width: 150,
      },
      {
        title: 'Over Due Value (₹)',
        dataIndex: 'overDueValue',
        key: 'overDueValue',
        width: 140,
      },
      {
        title: 'Avg. Aging Days',
        dataIndex: 'avgAgingDays',
        key: 'avgAgingDays',
        width: 140,
      },
      {
        title: "Paid % of Approved RA Bills",
        dataIndex: "paymentVsApprovedRaPct",
        key: "paymentVsApprovedRaPct",
        width: 200,
        render: (text: string) => (
          <span style={{ color: text.includes("100") ? "orange" : "green" }}>
            {text}
          </span>
        ),
      },
      {
        title: "Last Payment Date",
        dataIndex: "lastUpdated",
        key: "lastUpdated",
        width: 160,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
    ],
    filters: [
      {
        type: "date",
        label: "Payment Date Range",
        key: "paymentDateRange",
      },
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
          { label: "Campus C", value: "campus-c" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
    ],
    exportable: true,
  },
  "internal-approval-queue": {
    id: "internal-approval-queue",
    title: "Internal Approval Queue",
    columns: [
      {
        title: "WO #",
        dataIndex: "woNumber",
        key: "woNumber",
        fixed: "left",
        width: 120,
      },
      { title: "Project", dataIndex: "project", key: "project", width: 100 },
      {
        title: "Raised By",
        dataIndex: "raisedBy",
        key: "raisedBy",
        width: 120,
      },
      {
        title: "Pending With",
        dataIndex: "pendingWith",
        key: "pendingWith",
        width: 130,
      },
      {
        title: "Approval Stage",
        dataIndex: "approvalStage",
        key: "approvalStage",
        width: 130,
      },
      {
        title: "Raised On",
        dataIndex: "raisedOn",
        key: "raisedOn",
        width: 110,
      },
      {
        title: "SLA Due",
        dataIndex: "slaDue",
        key: "slaDue",
        width: 110,
        render: (text: string, record: any) => {
          const isOverdue = new Date(text) < new Date();
          return (
            <span style={{ color: isOverdue ? "red" : "orange" }}>{text}</span>
          );
        },
      },
      { title: "Value", dataIndex: "value", key: "value", width: 100 },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: string) => (
          <span
            style={{
              color:
                status === "Overdue"
                  ? "red"
                  : status === "Pending"
                    ? "orange"
                    : "green",
              fontWeight: "bold",
            }}
          >
            {status}
          </span>
        ),
      },
    ],
    filters: [
      {
        type: "select",
        label: "Approver",
        key: "pendingWith",
        options: [
          { label: "GM Projects", value: "gm-projects" },
          { label: "Finance Head", value: "finance-head" },
        ],
      },
      {
        type: "select",
        label: "Stage",
        key: "approvalStage",
        options: [
          { label: "Technical Review", value: "technical" },
          { label: "Finance Review", value: "finance" },
          { label: "Management Approval", value: "management" },
        ],
      },
      {
        type: "select",
        label: "SLA Breach",
        key: "slaBreach",
        options: [
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ],
      },
    ],
    exportable: true,
  },
  "contractor-acceptance-tracker": {
    id: "contractor-acceptance-tracker",
    title: "Contractor Acceptance Tracker",
    columns: [
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        width: 200,
      },
      {
        title: "Project",
        dataIndex: "projectName",
        key: "projectName",
        width: 200,
      },
      {
        title: "WO #",
        dataIndex: "woNumber",
        key: "workOrderSequenceId",
        fixed: "left",
        width: 150,
      },
      { title: "WO Value (₹)", dataIndex: "value", key: "value", width: 120 },
      {
        title: "Released On",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 150,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
      {
        title: "Accepted On",
        dataIndex: "approvalDate",
        key: "approvalDate",
        width: 150,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
    ],
    filters: [
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "DEF Build", value: "def-build" },
          { label: "ABC Infra", value: "abc-infra" },
        ],
      },
      {
        type: "text",
        label: "WO #",
        key: "woNumber",
      },
      {
        type: "select",
        label: "Status",
        key: "status",
        options: [
          { label: "Accepted", value: "accepted" },
          { label: "Pending", value: "pending" },
        ],
      },
    ],
    exportable: true,
  },
  "work-execution-pending-updates": {
    id: "work-execution-pending-updates",
    title: "Work Execution - Pending Updates",
    columns: [
      {
        title: "WO #",
        dataIndex: "woNumber",
        key: "woNumber",
        fixed: "left",
        width: 220,
      },
      { title: "Project", dataIndex: "project", key: "project", width: 220 },
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        width: 250,
      },
      { title: "Value", dataIndex: "value", key: "value", width: 120 },
      {
        title: "Planned %",
        dataIndex: "plannedPercent",
        key: "plannedPercent",
        width: 100,
      },
      {
        title: "Planned Value",
        dataIndex: "plannedValue",
        key: "plannedValue",
        width: 120,
      },
      {
        title: "Reported %",
        dataIndex: "reportedPercent",
        key: "reportedPercent",
        width: 110,
      },
      {
        title: "Reported Value",
        dataIndex: "reportedValue",
        key: "reportedValue",
        width: 130,
      },
      {
        title: "Pending %",
        dataIndex: "pendingPercent",
        key: "pendingPercent",
        width: 100,
      },
      {
        title: "Pending Value",
        dataIndex: "pendingValue",
        key: "pendingValue",
        width: 120,
      },
      {
        title: "Pending Update Since",
        dataIndex: "pendingUpdateSince",
        key: "pendingUpdateSince",
        width: 160,
        render: (days: number) => (
          <span
            style={{ color: days > 10 ? "red" : days > 5 ? "orange" : "green" }}
          >
            {days} days
          </span>
        ),
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Campus C", value: "campus-c" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "range",
        label: "Delay Days > X",
        key: "delayDays",
      },
    ],
    exportable: true,
  },
  "work-execution-approved-remaining": {
    id: "work-execution-approved-remaining",
    title: "Work Execution - Approved vs Remaining",
    columns: [
      {
        title: "WO #",
        dataIndex: "woNumber",
        key: "woNumber",
        fixed: "left",
        width: 220,
      },
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        width: 250,
      },
      {
        title: "Approved RA Bills",
        dataIndex: "rabillApprovedQty",
        key: "rabillApprovedQty",
        width: 140,
      },
      { title: "Value", dataIndex: "value", key: "value", width: 100 },
      {
        title: "Executed Qty",
        dataIndex: "executedQty",
        key: "executedQty",
        width: 120,
      },
      {
        title: "Executed Value",
        dataIndex: "executedValue",
        key: "executedValue",
        width: 130,
      },
      {
        title: "Approved Qty",
        dataIndex: "approvedQty",
        key: "approvedQty",
        width: 120,
      },
      {
        title: "Approved Value",
        dataIndex: "approvedValue",
        key: "approvedValue",
        width: 130,
      },
      {
        title: "Pending Approval Qty",
        dataIndex: "pendingApprovalQty",
        key: "pendingApprovalQty",
        width: 160,
      },
      {
        title: "Pending Approval Value",
        dataIndex: "pendingApprovalValue",
        key: "pendingApprovalValue",
        width: 170,
      },
      {
        title: "Balance Qty",
        dataIndex: "balanceQty",
        key: "balanceQty",
        width: 120,
      },
      {
        title: "Balance Value",
        dataIndex: "balanceValue",
        key: "balanceValue",
        width: 130,
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [{ label: "XYZ Construction", value: "xyz-construction" }],
      },
      {
        type: "date",
        label: "Date Range",
        key: "dateRange",
      },
    ],
    exportable: true,
  },
  "version-control-amendments": {
    id: "version-control-amendments",
    title: "Version Control & Amendments Register",
    columns: [
      {
        title: "Version #",
        dataIndex: "versionNumber",
        key: "versionNumber",
        fixed: "left",
        width: 120,
      },
      { title: "WO #", dataIndex: "woNumber", key: "woNumber", width: 220 },
      { title: "Type", dataIndex: "type", key: "type", width: 130 },
      {
        title: "Original Value",
        dataIndex: "originalValue",
        key: "originalValue",
        width: 130,
      },
      {
        title: "Impact Value",
        dataIndex: "impactValue",
        key: "impactValue",
        width: 120,
      },
      {
        title: "Impact %",
        dataIndex: "impactPercent",
        key: "impactPercent",
        width: 100,
      },
      {
        title: "Impact Timeline",
        dataIndex: "impactTimeline",
        key: "impactTimeline",
        width: 130,
      },
      {
        title: "Timeline %",
        dataIndex: "timelinePercent",
        key: "timelinePercent",
        width: 110,
      },
      {
        title: "Approved By",
        dataIndex: "approvedBy",
        key: "approvedBy",
        width: 140,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: string) => (
          <span
            style={{
              color:
                status === "Approved"
                  ? "green"
                  : status === "Pending"
                    ? "orange"
                    : "red",
              fontWeight: "bold",
            }}
          >
            {status}
          </span>
        ),
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [{ label: "ABC Infra", value: "abc-infra" }],
      },
      {
        type: "select",
        label: "Variation Type",
        key: "variationType",
        options: [
          { label: "Scope Change", value: "scope" },
          { label: "Cost Change", value: "cost" },
          { label: "Time Change", value: "time" },
        ],
      },
      {
        type: "date",
        label: "Date Range",
        key: "dateRange",
      },
    ],
    exportable: true,
  },
  "ra-bills-status-register": {
    id: "ra-bills-status-register",
    title: "RA Bills Status Register",
    columns: [
      {
        title: "RA Bill #",
        dataIndex: "raBillNumber",
        key: "raBillNumber",
        fixed: "left",
        width: 200,
      },
      {
        title: "WO #",
        dataIndex: "workOrderNumber",
        key: "workOrderNumber",
        width: 200,
      },
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        width: 220,
      },
      {
        title: "Billed Value",
        dataIndex: "billedValue",
        key: "billedValue",
        width: 120,
      },
      {
        title: "Approved Value",
        dataIndex: "raBillsApprovedValue",
        key: "raBillsApprovedValue",
        width: 130,
      },
      {
        title: "Payment Status",
        dataIndex: "paymentStatus",
        key: "paymentStatus",
        width: 130,
        render: (status: string) => (
          <span
            style={{
              color:
                status === "Paid"
                  ? "green"
                  : status === "Pending"
                    ? "orange"
                    : "blue",
              fontWeight: "bold",
            }}
          >
            {status}
          </span>
        ),
      },
    ],
    filters: [
      {
        type: "text",
        label: "WO #",
        key: "woNumber",
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "text",
        label: "RA Bill #",
        key: "raBillNumber",
      },
      {
        type: "select",
        label: "Status",
        key: "paymentStatus",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
          { label: "Paid", value: "paid" },
        ],
      },
    ],
    exportable: true,
  },
  "ra-bill-aging": {
    id: "ra-bill-aging",
    title: "RA Bill Aging",
    columns: [
      {
        title: "RA Bill #",
        dataIndex: "raBillNumber",
        key: "raBillNumber",
        fixed: "left",
        width: 220,
      },
      { title: "WO #", dataIndex: "woNumber", key: "woNumber", width: 220 },
      {
        title: "Submitted On",
        dataIndex: "submittedOn",
        key: "submittedOn",
        width: 120,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
      {
        title: "Overdue (Days)",
        dataIndex: "overdueDays",
        key: "overdueDays",
        width: 130,
        render: (days: number) => (
          <span
            style={{
              color: days > 45 ? "red" : days > 30 ? "orange" : "#c0c006",
            }}
          >
            {days}
          </span>
        ),
      },
      {
        title: "Pending Stage",
        dataIndex: "pendingStage",
        key: "pendingStage",
        width: 120,
      },
      { title: "Value", dataIndex: "value", key: "value", width: 120 },
      { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 200 },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Campus C", value: "campus-c" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "select",
        label: "Age Buckets",
        key: "ageBuckets",
        options: [
          { label: "0-15 days", value: "0-15" },
          { label: "16-30 days", value: "16-30" },
          { label: "31-45 days", value: "31-45" },
          { label: ">45 days", value: "45+" },
        ],
      },
    ],
    exportable: true,
  },
  "deductions-ledger": {
    id: "deductions-ledger",
    title: "Deductions Ledger",
    columns: [
      {
        title: "WO #",
        dataIndex: "woNumber",
        key: "woNumber",
        fixed: "left",
        width: 220,
      },
      {
        title: "RA Bill #",
        dataIndex: "raBillNumber",
        key: "raBillNumber",
        width: 220,
      },
      {
        title: "RA Bill Value",
        dataIndex: "raBillValue",
        key: "raBillValue",
        width: 130,
      },
      {
        title: "Deduction Type",
        dataIndex: "deductionType",
        key: "deductionType",
        width: 130,
      },
      {
        title: "Deduction Value",
        dataIndex: "deductionValue",
        key: "deductionValue",
        width: 140,
      },
      { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 250 },
    ],
    filters: [
      {
        type: "text",
        label: "WO #",
        key: "woNumber",
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "select",
        label: "Deduction Type",
        key: "deductionType",
        options: [
          { label: "Retention", value: "retention" },
          { label: "On Hold", value: "on-hold" },
        ],
      },
    ],
    exportable: true,
  },
  "payments-register": {
    id: "payments-register",
    title: "Payments Register",
    columns: [
      {
        title: "Voucher #",
        dataIndex: "voucherNumber",
        key: "voucherNumber",
        fixed: "left",
        width: 220,
      },
      { title: "WO #", dataIndex: "woNumber", key: "woNumber", width: 220 },
      {
        title: "RA Bill #",
        dataIndex: "raBillNumber",
        key: "raBillNumber",
        width: 220,
      },
      {
        title: "Paid Value",
        dataIndex: "paidValue",
        key: "paidValue",
        width: 120,
      },
      {
        title: "Payment Date",
        dataIndex: "paymentDate",
        key: "paymentDate",
        width: 120,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
      { title: "Mode", dataIndex: "mode", key: "mode", width: 100 },
      {
        title: "Created By",
        dataIndex: "createdBy",
        key: "createdBy",
        width: 140,
      },
      {
        title: "Approved By",
        dataIndex: "approvedBy",
        key: "approvedBy",
        width: 140,
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "date",
        label: "Payment Date",
        key: "paymentDate",
      },
      {
        type: "select",
        label: "Bank",
        key: "bank",
        options: [
          { label: "HDFC Bank", value: "hdfc" },
          { label: "ICICI Bank", value: "icici" },
        ],
      },
    ],
    exportable: true,
  },
  "outstanding-aging": {
    id: "outstanding-aging",
    title: "Outstanding & Aging",
    columns: [
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        fixed: "left",
        width: 250,
      },
      {
        title: "Outstanding Value",
        dataIndex: "outstandingValue",
        key: "outstandingValue",
        width: 150,
      },
      {
        title: "0-30 Days",
        dataIndex: "days0to30",
        key: "days0to30",
        width: 120,
      },
      {
        title: "0-30 Days %",
        dataIndex: "days0to30Percent",
        key: "days0to30Percent",
        width: 120,
      },
      {
        title: "31-60 Days",
        dataIndex: "days31to60",
        key: "days31to60",
        width: 120,
      },
      {
        title: "31-60 Days %",
        dataIndex: "days31to60Percent",
        key: "days31to60Percent",
        width: 120,
      },
      {
        title: "61-90 Days",
        dataIndex: "days61to90",
        key: "days61to90",
        width: 120,
      },
      {
        title: "61-90 Days %",
        dataIndex: "days61to90Percent",
        key: "days61to90Percent",
        width: 120,
      },
      {
        title: "Oldest Pending",
        dataIndex: "oldestPending",
        key: "oldestPending",
        width: 130,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "select",
        label: "Age Buckets",
        key: "ageBuckets",
        options: [
          { label: "0-30 days", value: "0-30" },
          { label: "31-60 days", value: "31-60" },
          { label: "61-90 days", value: "61-90" },
          { label: ">90 days", value: "90+" },
        ],
      },
    ],
    exportable: true,
  },
  "wo-vs-budget": {
    id: "wo-vs-budget",
    title: "WO vs Budget",
    columns: [
      {
        title: "Project",
        dataIndex: "project",
        key: "project",
        fixed: "left",
        width: 120,
      },
      {
        title: "Budget Head",
        dataIndex: "budgetHead",
        key: "budgetHead",
        width: 120,
      },
      {
        title: "Budget Allocated",
        dataIndex: "budgetAllocated",
        key: "budgetAllocated",
        width: 140,
      },
      {
        title: "WO Committed",
        dataIndex: "woCommitted",
        key: "woCommitted",
        width: 130,
      },
      {
        title: "Variance",
        dataIndex: "variance",
        key: "variance",
        width: 120,
        // render: (text: string) => (
        //   <span style={{ color: text.startsWith("+") ? "green" : "red" }}>
        //     {text}
        //   </span>
        // ),
      },
      {
        title: "Variance %",
        dataIndex: "variancePercent",
        key: "variancePercent",
        width: 120,
        // render: (text: string) => (
        //   <span style={{ color: text.startsWith("+") ? "green" : "red" }}>
        //     {text}
        //   </span>
        // ),
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Cost Head",
        key: "budgetHead",
        options: [
          { label: "Civil", value: "civil" },
          { label: "MEP", value: "mep" },
          { label: "Finishing", value: "finishing" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
    ],
    exportable: true,
    drillDowns: ["wo-portfolio-summary"],
  },
  "tax-summary": {
    id: "tax-summary",
    title: "Tax Summary",
    columns: [
      {
        title: "Contractor Name",
        dataIndex: "contractor",
        key: "contractor",
        fixed: "left",
        width: 200,
      },
      {
        title: "Work Order Value",
        dataIndex: "workOrderValue",
        key: "workOrderValue",
        width: 150,
      },
      {
        title: "Tax Amount",
        dataIndex: "taxAmount",
        key: "taxAmount",
        width: 130,
      },
    ],
    filters: [
      {
        type: "select",
        label: "Project",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "SLN Buildcon", value: "sln-buildcon" },
          { label: "ABC Infra", value: "abc-infra" },
        ],
      },
      {
        type: "select",
        label: "Tax Type",
        key: "taxType",
        options: [
          { label: "GST", value: "gst" },
          { label: "TDS", value: "tds" },
        ],
      },
    ],
    exportable: true,
    drillDowns: ["tax-summary-detail"],
  },
  "tax-summary-detail": {
    id: "tax-summary-detail",
    title: "Tax Summary - Drill Down Report",
    columns: [
      {
        title: "WO #",
        dataIndex: "woNumber",
        key: "woNumber",
        fixed: "left",
        width: 120,
      },
      {
        title: "Contractor Name",
        dataIndex: "contractor",
        key: "contractor",
        width: 150,
      },
      {
        title: "RA Bill #",
        dataIndex: "raBillNumber",
        key: "raBillNumber",
        width: 120,
      },
      { title: "Tax Type", dataIndex: "taxType", key: "taxType", width: 100 },
      {
        title: "Rate %",
        dataIndex: "ratePercent",
        key: "ratePercent",
        width: 100,
      },
      {
        title: "Tax Amount",
        dataIndex: "taxAmount",
        key: "taxAmount",
        width: 130,
      },
    ],
    exportable: true,
  },
  "audit-trail": {
    id: "audit-trail",
    title: "Audit Trail",
    columns: [
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        fixed: "left",
        width: 100,
      },
      { title: "Ref #", dataIndex: "refNumber", key: "refNumber", width: 120 },
      { title: "Event", dataIndex: "event", key: "event", width: 120 },
      { title: "User", dataIndex: "user", key: "user", width: 140 },
      { title: "Role", dataIndex: "role", key: "role", width: 120 },
      {
        title: "Timestamp",
        dataIndex: "timestamp",
        key: "timestamp",
        width: 140,
      },
      {
        title: "IP Address",
        dataIndex: "ipAddress",
        key: "ipAddress",
        width: 130,
      },
    ],
    filters: [
      {
        type: "text",
        label: "WO #",
        key: "woNumber",
      },
      {
        type: "text",
        label: "User",
        key: "user",
      },
      {
        type: "select",
        label: "Action Type",
        key: "event",
        options: [
          { label: "Created", value: "created" },
          { label: "Modified", value: "modified" },
          { label: "Approved", value: "approved" },
          { label: "Payment Released", value: "payment-released" },
        ],
      },
      {
        type: "date",
        label: "Date Range",
        key: "dateRange",
      },
    ],
    exportable: true,
  },
  "budget-variance-report": {
    id: "budget-variance-report",
    title: "Budget Variance - Executed & In-Progress",
    columns: [
      {
        title: "Project",
        dataIndex: "project",
        key: "project",
        fixed: "left",
        width: 120,
      },
      {
        title: "Budget Allocated",
        dataIndex: "budgetAllocated",
        key: "budgetAllocated",
        width: 140,
      },
      { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120 },
      {
        title: "Executed Budget",
        dataIndex: "executedBudget",
        key: "executedBudget",
        width: 140,
      },
      {
        title: "Executed Actual",
        dataIndex: "executedActual",
        key: "executedActual",
        width: 140,
      },
      {
        title: "Var % (Exec)",
        dataIndex: "varExec",
        key: "varExec",
        width: 120,
        // render: (text: string) => (
        //   <span style={{ color: text.startsWith("-") ? "green" : "red" }}>
        //     {text}
        //   </span>
        // ),
      },
      {
        title: "In-Prog Budget",
        dataIndex: "inProgBudget",
        key: "inProgBudget",
        width: 140,
      },
      {
        title: "In-Prog Forecast",
        dataIndex: "inProgForecast",
        key: "inProgForecast",
        width: 150,
      },
      {
        title: "Var % (In-Prog)",
        dataIndex: "varInProg",
        key: "varInProg",
        width: 130,
        render: (text: string) => (
          <span style={{ color: text.startsWith("+") ? "red" : "green" }}>
            {text}
          </span>
        ),
      },
    ],
    filters: [
      {
        type: "date",
        label: "Date",
        key: "dateRange",
      },
      {
        type: "select",
        label: "Project/Site",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "select",
        label: "Slice",
        key: "slice",
        options: [
          { label: "Executed", value: "executed" },
          { label: "In-Progress", value: "in-progress" },
          { label: "Both", value: "both" },
        ],
      },
    ],
    exportable: true,
  },
  "master-status-counts": {
    id: "master-status-counts",
    title: "Master Status Counts (Tiles + Table)",
    columns: [
      {
        title: "Contractor",
        dataIndex: "contractor",
        key: "contractor",
        fixed: "left",
        width: 150,
      },
      { title: "Budget", dataIndex: "budget", key: "budget", width: 120 },
      { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120 },
      { title: "Draft", dataIndex: "draft", key: "draft", width: 80 },
      { title: "Approval", dataIndex: "approval", key: "approval", width: 90 },
      { title: "Released", dataIndex: "released", key: "released", width: 90 },
      { title: "Accepted", dataIndex: "accepted", key: "accepted", width: 90 },
      { title: "Active", dataIndex: "active", key: "active", width: 80 },
      { title: "Amended", dataIndex: "amended", key: "amended", width: 90 },
      { title: "Closed", dataIndex: "closed", key: "closed", width: 80 },
      {
        title: "Cancelled",
        dataIndex: "cancelled",
        key: "cancelled",
        width: 90,
      },
      { title: "Total", dataIndex: "total", key: "total", width: 80 },
    ],
    filters: [
      {
        type: "select",
        label: "Project/Site",
        key: "project",
        options: [
          { label: "Tower A", value: "tower-a" },
          { label: "Tower B", value: "tower-b" },
        ],
      },
      {
        type: "select",
        label: "Contractor",
        key: "contractor",
        options: [
          { label: "ABC Infra", value: "abc-infra" },
          { label: "XYZ Works", value: "xyz-works" },
        ],
      },
      {
        type: "multiSelect",
        label: "Status",
        key: "status",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Approval", value: "approval" },
          { label: "Released", value: "released" },
          { label: "Accepted", value: "accepted" },
          { label: "Active", value: "active" },
          { label: "Amended", value: "amended" },
          { label: "Closed", value: "closed" },
          { label: "Cancelled", value: "cancelled" },
        ],
      },
    ],
    exportable: true,
  },
  "contractor-ledger": {
    id: "contractor-ledger",
    title: "Contractor Ledger",
    columns: [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 120,
        render: (text: string) => (
          <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
        ),
      },
      {
        title: "Particulars",
        dataIndex: "particulars",
        key: "particulars",
        width: 150,
      },
      {
        title: "Voucher No",
        dataIndex: "voucherNo",
        key: "voucherNo",
        width: 200,
      },
      {
        title: "Debit",
        dataIndex: "debit",
        key: "debit",
        width: 120,
        render: (text: string) => (
          <span style={{ color: text !== "₹0" ? "#cf1322" : "#666" }}>
            {text}
          </span>
        ),
      },
      {
        title: "Credit",
        dataIndex: "credit",
        key: "credit",
        width: 120,
        render: (text: string) => (
          <span style={{ color: text !== "₹0" ? "#389e0d" : "#666" }}>
            {text}
          </span>
        ),
      },
      {
        title: "Balance",
        dataIndex: "balance",
        key: "balance",
        width: 120,
        render: (text: string) => (
          <span style={{ fontWeight: "bold" }}>{text}</span>
        ),
      },
    ],
    exportable: true,
  },
};

export default {
  workOrderReports,
  reportConfigs,
};
